import FirebaseService from "../firebase/FirebaseService";
import { analytics } from "../firebase/firebase";
import AnalyticsEvent, { AnalyticsEventProps } from "../../interfaces/AnalyticsEvent";
import User from "../../interfaces/User";
import { currentUserEmail, currentUserId } from "../../App";

const RETRY_COOLDOWNS = [0.1, 1, 3];

const sleep = (time: number): Promise<void> =>
  new Promise(resolve => {
    setTimeout(() => resolve(), time);
  });

class AnalyticsService {
  // Log a general analytics event
  static async logToDb(eventProps: AnalyticsEventProps): Promise<AnalyticsEvent | null> {
    const { retryNumber = 0 } = eventProps;
    try {
      const result = await FirebaseService.logAnalyticsEvent(eventProps);
      return result;
    } catch (error) {
      if (retryNumber < RETRY_COOLDOWNS.length) {
        await sleep((RETRY_COOLDOWNS?.[retryNumber] || 3) * 1000);
        return AnalyticsService.log({ ...eventProps, retryNumber: retryNumber + 1 });
      } else {
        console.error("Failed to send event to analytics:", eventProps);
        return null;
      }
    }
  }

  static async logToGa(eventProps: AnalyticsEventProps): Promise<void> {
    analytics.logEvent(eventProps.eventName, eventProps);
  }

  static async log(baseEventProps: AnalyticsEventProps): Promise<AnalyticsEvent | null> {
    const currentTime = new Date();
    const eventProps = {
      ...baseEventProps,
      retryNumber: baseEventProps.retryNumber || 0,
      timeStamp: currentTime.valueOf(),
      time: currentTime.toLocaleString(),
      userEmail: currentUserEmail,
      userUid: currentUserId,
    };
    const [analyticsEvent, _] = await Promise.all([
      AnalyticsService.logToDb(eventProps),
      AnalyticsService.logToGa(eventProps),
    ]);
    return analyticsEvent;
  }

  static async logUserCreated(user: User) {
    await AnalyticsService.log({
      eventName: "sign_up",
      eventValue: user.uid,
      eventCategory: "general",
      extendedValues: {
        firstName: user.firstName,
        lastName: user.lastName,
        displayName: user.displayName,
        email: user.email,
        userType: user.userType,
      },
    });
  }

  // Log a screen view
  static async logCustomScreenView(page: string, url: string, parameters: string) {
    await AnalyticsService.log({
      eventName: "page_view",
      eventValue: page,
      eventCategory: "general",
      extendedValues: {
        url: url,
        parameters: parameters,
      },
    });
  }

  // Log a screen view
  static async logScreenView() {
    await AnalyticsService.logCustomScreenView(window.location.pathname, window.location.href, window.location.search);
  }
}

export default AnalyticsService;
